import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

// IDB resume store. We persist *only* what we cannot recover from S3 cheaply:
//   - photoId / uploadId  → assigned by /uploads/batch, lost on tab close
//   - completedParts      → cuts the resume cost from "re-upload all" to
//                           "list parts and skip"
// We do NOT persist the encrypted bytes — re-encrypting from the original File
// is faster than serialising 100s of MB into IDB, and the user keeps the file
// handle in DOM state during the upload.

const DB_NAME = 'photo-vault-uploads';
const DB_VERSION = 1;
const STORE = 'sessions';

export type FileStatus = 'pending' | 'encrypted' | 'uploading' | 'complete' | 'failed';

export interface PersistedFile {
  clientFileId: string;
  filename: string;
  size: number;
  status: FileStatus;
  // Set after /uploads/batch responds for this file.
  photoId?: string;
  uploadId?: string;
  partsCount?: number;
  completedParts: { partNumber: number; etag: string }[];
  error?: string;
}

export interface PersistedSession {
  id: string;
  albumId: string;
  createdAt: string;
  updatedAt: string;
  files: PersistedFile[];
}

interface UploadsSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: PersistedSession;
    indexes: { byAlbum: string };
  };
}

let dbPromise: Promise<IDBPDatabase<UploadsSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<UploadsSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<UploadsSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('byAlbum', 'albumId');
      },
    });
  }
  return dbPromise;
}

export async function saveSession(session: PersistedSession): Promise<void> {
  const db = await getDb();
  await db.put(STORE, { ...session, updatedAt: new Date().toISOString() });
}

export async function getSession(id: string): Promise<PersistedSession | undefined> {
  const db = await getDb();
  return db.get(STORE, id);
}

export async function listAlbumSessions(albumId: string): Promise<PersistedSession[]> {
  const db = await getDb();
  return db.getAllFromIndex(STORE, 'byAlbum', albumId);
}

export async function deleteSession(id: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, id);
}

export function newSessionId(): string {
  return crypto.randomUUID();
}
